import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, ShieldCheck, ShieldOff, UserPlus, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { platformApi } from '../api'
import { Modal } from '../components/Modal'
import { StatusPill, operatorTone } from '../components/StatusPill'
import { usePlatformAuthStore } from '../store'
import type { OperatorDetail, OperatorRole } from '../types'

const ROLES: Array<{ value: OperatorRole; label: string; blurb: string }> = [
  { value: 'OWNER', label: 'Owner', blurb: 'Everything, including managing operators' },
  { value: 'SUPPORT', label: 'Support', blurb: 'Suspend and resume. No pricing' },
  { value: 'BILLING', label: 'Billing', blurb: 'Plans, subscriptions and invoices' },
  { value: 'READ_ONLY', label: 'Read only', blurb: 'Sees the estate and nothing else' },
]

/** Who can administer this installation. */
export function PlatformOperatorsPage() {
  const queryClient = useQueryClient()
  const me = usePlatformAuthStore((state) => state.operator)
  const isOwner = me?.role === 'OWNER'

  const [inviting, setInviting] = useState(false)
  const [passwordFor, setPasswordFor] = useState<OperatorDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const operators = useQuery({
    queryKey: ['platform', 'operators'],
    queryFn: () => platformApi.operators(),
  })

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['platform', 'operators'] })
  }

  /** Every mutation here can be refused by a guard, so they all surface the reason. */
  function onError(err: unknown) {
    const detail = (err as { response?: { data?: { detail?: string; title?: string } } }).response
      ?.data
    setError(detail?.detail ?? detail?.title ?? 'That did not work.')
  }

  const invite = useMutation({
    mutationFn: (form: { email: string; fullName: string; role: OperatorRole }) =>
      platformApi.inviteOperator(form.email, form.fullName, form.role),
    onSuccess: (_result, form) => {
      setInviting(false)
      setError(null)
      setNotice(
        `${form.email} was invited. They cannot sign in until you set a password for them.`,
      )
      refresh()
    },
    onError,
  })

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: OperatorRole }) =>
      platformApi.setOperatorRole(id, role),
    onSuccess: () => {
      setError(null)
      refresh()
    },
    onError,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'DISABLED' }) =>
      platformApi.setOperatorStatus(id, status),
    onSuccess: () => {
      setError(null)
      refresh()
    },
    onError,
  })

  const setPassword = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      platformApi.setOperatorPassword(id, password),
    onSuccess: () => {
      setPasswordFor(null)
      setError(null)
      setNotice('Password set. They must change it when they first sign in.')
      refresh()
    },
    onError,
  })

  const rows = operators.data ?? []
  const active = rows.filter((row) => row.status === 'ACTIVE').length
  const pending = rows.filter((row) => !row.canSignIn && row.status === 'ACTIVE').length

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="text-xl font-semibold text-[var(--color-heading)]">Operators</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Everyone who can administer this installation.
          </p>
        </div>
        {isOwner && (
          <Button className="ms-auto" onClick={() => setInviting(true)}>
            <UserPlus className="me-2 size-4" aria-hidden />
            Invite operator
          </Button>
        )}
      </div>

      <dl className="grid gap-4 sm:grid-cols-3">
        <Tile label="Operators" value={rows.length} />
        <Tile label="Active" value={active} />
        <Tile label="Awaiting a password" value={pending} caption="invited, cannot sign in yet" />
      </dl>

      {notice && (
        <p className="rounded-[var(--radius-card)] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
          {notice}
        </p>
      )}
      {error && (
        <p
          role="alert"
          className="rounded-[var(--radius-card)] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200"
        >
          {error}
        </p>
      )}

      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
        <table className="w-full min-w-[46rem] text-sm">
          <thead className="bg-[var(--color-page)] text-xs uppercase text-[var(--color-muted)]">
            <tr>
              <th className="px-4 py-3 text-start font-medium">Operator</th>
              <th className="px-4 py-3 text-start font-medium">Role</th>
              <th className="px-4 py-3 text-start font-medium">State</th>
              <th className="px-4 py-3 text-start font-medium">Last sign-in</th>
              <th className="px-4 py-3 text-end font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {operators.isPending && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-[var(--color-muted)]">
                  Loading…
                </td>
              </tr>
            )}

            {!operators.isPending && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center">
                  <Users className="mx-auto mb-2 size-6 text-[var(--color-muted)]" aria-hidden />
                  <p className="text-sm text-[var(--color-muted)]">No operators yet.</p>
                </td>
              </tr>
            )}

            {rows.map((operator) => {
              const isMe = operator.id === me?.id
              return (
                <tr key={operator.id} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[var(--color-heading)]">
                      {operator.fullName}
                      {isMe && (
                        <span className="ms-2 text-xs font-normal text-[var(--color-muted)]">
                          you
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--color-muted)]">{operator.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {isOwner && !isMe ? (
                      <select
                        aria-label={`Role for ${operator.email}`}
                        value={operator.role}
                        onChange={(event) =>
                          setRole.mutate({
                            id: operator.id,
                            role: event.target.value as OperatorRole,
                          })
                        }
                        className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1 text-sm"
                      >
                        {ROLES.map((role) => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{ROLES.find((r) => r.value === operator.role)?.label}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill tone={operatorTone(operator.status, operator.canSignIn)}>
                      {operator.status === 'DISABLED'
                        ? 'Disabled'
                        : operator.canSignIn
                          ? 'Active'
                          : 'Invited'}
                    </StatusPill>
                    {operator.status === 'ACTIVE' && !operator.canSignIn && (
                      <div className="mt-1 text-xs text-[var(--color-muted)]">
                        needs a password
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {operator.lastLoginAt
                      ? new Date(operator.lastLoginAt).toLocaleString()
                      : 'never'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      {isOwner && !isMe && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPasswordFor(operator)}
                          >
                            <KeyRound className="me-1.5 size-4" aria-hidden />
                            Set password
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setStatus.mutate({
                                id: operator.id,
                                status: operator.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE',
                              })
                            }
                          >
                            {operator.status === 'ACTIVE' ? (
                              <>
                                <ShieldOff className="me-1.5 size-4" aria-hidden />
                                Disable
                              </>
                            ) : (
                              <>
                                <ShieldCheck className="me-1.5 size-4" aria-hidden />
                                Enable
                              </>
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-[var(--color-muted)]">
        There is no delete. The audit trail names the operator who suspended a customer or
        repriced a plan, so an account that has acted is disabled rather than removed.
      </p>

      <InviteModal
        open={inviting}
        pending={invite.isPending}
        onClose={() => setInviting(false)}
        onSubmit={(form) => invite.mutate(form)}
      />

      <PasswordModal
        operator={passwordFor}
        pending={setPassword.isPending}
        onClose={() => setPasswordFor(null)}
        onSubmit={(password) =>
          passwordFor && setPassword.mutate({ id: passwordFor.id, password })
        }
      />
    </div>
  )
}

function InviteModal({
  open,
  pending,
  onClose,
  onSubmit,
}: {
  open: boolean
  pending: boolean
  onClose: () => void
  onSubmit: (form: { email: string; fullName: string; role: OperatorRole }) => void
}) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<OperatorRole>('SUPPORT')

  return (
    <Modal
      open={open}
      title="Invite an operator"
      description="They get an account immediately, and a password you set for them afterwards — this installation has no operator mail."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending || !email.trim() || !fullName.trim()}
            onClick={() => onSubmit({ email: email.trim(), fullName: fullName.trim(), role })}
          >
            {pending ? 'Inviting…' : 'Invite'}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <Label htmlFor="operator-email">Email</Label>
        <Input
          id="operator-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="name@company.com"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="operator-name">Full name</Label>
        <Input
          id="operator-name"
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
        />
      </div>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-[var(--color-heading)]">Role</legend>
        {ROLES.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-start gap-3 rounded-md border border-[var(--color-border)] p-3 text-sm has-[:checked]:border-[var(--color-primary)]"
          >
            <input
              type="radio"
              name="operator-role"
              value={option.value}
              checked={role === option.value}
              onChange={() => setRole(option.value)}
              className="mt-1"
            />
            <span>
              <span className="font-medium text-[var(--color-heading)]">{option.label}</span>
              <span className="block text-xs text-[var(--color-muted)]">{option.blurb}</span>
            </span>
          </label>
        ))}
      </fieldset>
    </Modal>
  )
}

function PasswordModal({
  operator,
  pending,
  onClose,
  onSubmit,
}: {
  operator: OperatorDetail | null
  pending: boolean
  onClose: () => void
  onSubmit: (password: string) => void
}) {
  const [password, setPassword] = useState('')

  return (
    <Modal
      open={operator !== null}
      title={`Set a password for ${operator?.fullName ?? ''}`}
      description="Send it to them out of band. They must change it when they first sign in."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={pending || password.length < 8} onClick={() => onSubmit(password)}>
            {pending ? 'Setting…' : 'Set password'}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <Label htmlFor="operator-password">New password</Label>
        <Input
          id="operator-password"
          type="text"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="At least 8 characters, with a capital, a digit and a symbol"
        />
        <p className="text-xs text-[var(--color-muted)]">
          Shown as text on purpose: you have to copy it somewhere to send it.
        </p>
      </div>
    </Modal>
  )
}

function Tile({ label, value, caption }: { label: string; value: number; caption?: string }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <dt className="text-xs uppercase text-[var(--color-muted)]">{label}</dt>
      <dd className="mt-2 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
        {value.toLocaleString()}
      </dd>
      {caption && <p className="text-xs text-[var(--color-muted)]">{caption}</p>}
    </div>
  )
}
